import { useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent } from '../../components/ui/card';
import { Download, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import usePageBanner from '../../hooks/usePageBanner';
import { AutoTranslate } from '../../hooks/useAutoTranslate';

const MedlemskapPage = () => {
  const { language, isRTL } = useLanguage();
  const { bannerImage, bannerTitle, bannerSubtitle } = usePageBanner('medlemskap', language);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen themed-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />

      {/* Header */}
      <section className="themed-hero relative h-80 overflow-hidden">
        <img
          src={bannerImage || "https://images.unsplash.com/photo-1529070538774-1843cb3265df?w=1920"}
          alt="Medlemskap"
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 bg-gradient-to-r ${isRTL ? 'from-black/40 to-black/70' : 'from-black/70 to-black/40'} flex items-center`}>
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full ${isRTL ? 'text-right' : ''}`}>
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4 fade-in">
              {bannerTitle || <AutoTranslate>Medlemskap</AutoTranslate>}
            </h1>
            <p className="text-xl text-gray-200 fade-in">
              {bannerSubtitle || <AutoTranslate>Bli en del av den katolska gemenskapen</AutoTranslate>}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Om medlemskap */}
          <Card className="border-0 shadow-xl card-hover mb-8">
            <CardContent className="p-8">
              <h2 className="text-3xl font-serif font-bold text-gray-900 mb-6">
                <AutoTranslate>Om medlemskap</AutoTranslate>
              </h2>
              
              <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
                <p className="leading-relaxed">
                  <AutoTranslate>Medlem i Kyrkan blir du genom ditt dop.</AutoTranslate>
                </p>
                
                <p className="leading-relaxed">
                  <AutoTranslate>
                    Men att bli registrerad måste du göra själv i din lokala kyrka (i fall du inte är döpt i den kyrka som du nu tillhör genom din nuvarande adress). För i tiden var det frågan om att skriva in sig i församlingens sk. "kartotek".
                  </AutoTranslate>
                </p>
                
                <p className="leading-relaxed">
                  <AutoTranslate>
                    Idag är registreringen mer komplex, med tanke på att informationen skickas till Katolska biskopsämbetet i Stockholm, där det i sin tur skickas vidare till Skatteverket. På det sättet tar Skatteverket ut en skatt på 1% av din årsinkomst (om du är över 18 år). Dessa pengar går i sin tur tillbaka till Katolska biskopsämbetet.
                  </AutoTranslate>
                </p>
                
                <p className="leading-relaxed">
                  <AutoTranslate>
                    Katolska biskopsämbetet (Katolska Stiftet) fördelar pengarna till olika församlingar med utgångspunkt för hur många medlemmar som är registrerade i varje församling. Det betyder att varje församling får mer pengar desto högre antal registrerade medlemmar församlingen har. Dina pengar går alltså tillbaka till din församling om du är registrerad i din lokala katolska församling. En av de fem kyrkliga budorden berör också denna aspekt, nämligen att värna och stödja - även ekonomiskt - sin församling. Detta är en del av den kristna identiteten.
                  </AutoTranslate>
                </p>
                <p className="leading-relaxed">
                  <AutoTranslate>
                    Kyrkoherden
                  </AutoTranslate>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Download Section */}
          <Card className="border-0 shadow-xl card-hover bg-gradient-to-r from-amber-500 to-orange-500 text-white">
            <CardContent className="p-8">
              <div className="flex items-center justify-center gap-3 mb-6">
                <FileText className="w-8 h-8" />
                <h2 className="text-2xl font-serif font-bold">
                  <AutoTranslate>Medlemsregistrering</AutoTranslate>
                </h2>
              </div>
              <p className="text-center text-white/90 mb-6">
                <AutoTranslate>
                  Ladda ner blanketten, fyll i den och lämna in till din lokala församling.
                </AutoTranslate>
              </p>
              <div className="flex justify-center">
                <a 
                  href="https://customer-assets.emergentagent.com/job_d70d68ad-5bce-4651-97c2-3539a23c6a1c/artifacts/24n83p0d_blankett-till-medlemsregistret.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="membership-download-btn"
                >
                  <Button 
                    variant="secondary" 
                    size="lg"
                    className={`flex items-center gap-2 bg-white text-amber-700 hover:bg-gray-100 ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    <Download className="w-5 h-5" />
                    <AutoTranslate>Ladda ner medlemsblankett (PDF)</AutoTranslate>
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MedlemskapPage;
